import type { AiAnnotationDraft, AiProposalEngine } from "@/ai/engines/engine.types";
import { inferProposalTimeSec, proposalHasKeyword } from "@/ai/engines/engineUtils";

const AUDIO_KEYWORDS = ["audio", "dialogue", "mix", "motif", "sound", "sonic", "music"];

export const audioEngine: AiProposalEngine = (context) => {
  const drafts: AiAnnotationDraft[] = [];
  const sonics = context.narrativeSonicsReadModel;

  if (sonics?.derivedContinuityWarningCount && sonics.derivedContinuityWarningCount > 0) {
    drafts.push({
      type: "audio",
      subtype: "sound_continuity_mismatch",
      severity: sonics.derivedContinuityWarningCount > 2 ? "warning" : "suggestion",
      summary: sonics.continuitySummary?.trim() || "Narrative sonics detected audio continuity pressure.",
      details: {
        roomToneProfile: sonics.focusScope ?? null,
        ambientShift: sonics.continuitySummary ?? null,
        fixStrategy: sonics.fxSummary ?? null,
      },
      proposedAction: "review",
      nodeId: `narrative-sonics:${context.selectedClipId ?? "global"}`,
      timeSec: sonics.currentTimeSec ?? context.anchorTimeSec,
      frame: Math.floor((sonics.currentTimeSec ?? context.anchorTimeSec) * context.fps),
      action: "Review sonic continuity",
    });
  }

  if (sonics?.emotionalSummary || sonics?.fxSummary) {
    drafts.push({
      type: "audio",
      subtype: "emotional_audio_treatment",
      severity: "suggestion",
      summary: sonics?.emotionalSummary?.trim() || "Emotional audio treatment available.",
      details: {
        emotion: sonics?.emotionalSummary ?? null,
        treatmentType: sonics?.fxSummary ?? null,
        intensity: sonics?.continuityScore01 ?? null,
      },
      proposedAction: "adjust",
      nodeId: `narrative-sonics:treatment:${context.selectedClipId ?? "global"}`,
      timeSec: sonics?.currentTimeSec ?? context.anchorTimeSec,
      frame: Math.floor((sonics?.currentTimeSec ?? context.anchorTimeSec) * context.fps),
      action: "Tune emotional sound treatment",
    });
  }

  for (const proposal of context.proposals) {
    if (!proposalHasKeyword(proposal, AUDIO_KEYWORDS)) continue;

    const timeSec = inferProposalTimeSec(context, proposal);
    const subtype = proposal.rationale.toLowerCase().includes("dialogue") ? "dialogue_clarity_issue" : "audio_motif_proposal";
    drafts.push({
      type: "audio",
      subtype,
      severity: (proposal.confidence ?? 0) >= 0.75 ? "warning" : "suggestion",
      summary: proposal.rationale.trim() || "Audio review recommended.",
      details: subtype === "dialogue_clarity_issue"
        ? {
            lineId: proposal.id,
            maskingSource: context.selectedClipLabel,
            suggestedMixAdjustment: "raise_dialogue_presence",
          }
        : {
            motifId: proposal.id,
            emotionalAlignment: context.narrativeSummary || null,
            placementFrame: Math.floor(timeSec * context.fps),
          },
      proposedAction: subtype === "dialogue_clarity_issue" ? "adjust" : "apply_variant",
      nodeId: proposal.id,
      timeSec,
      frame: Math.floor(timeSec * context.fps),
      action: subtype === "dialogue_clarity_issue" ? "Adjust dialogue intelligibility" : "Try the proposed audio motif",
    });
  }

  return drafts;
};